/**
 * Icon system — hand-tuned SVGs at 1em sizing. No third-party library.
 *
 * Design: 16×16 grid, 1.5px stroke, rounded caps and joins,
 * stroke="currentColor" so color inherits from the parent text.
 *
 * Add new icons sparingly — each one is a considered mark, not a label.
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number | string };

function Icon({ size = 16, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconPlus(p: IconProps) {
  return <Icon {...p}><path d="M8 3v10M3 8h10" /></Icon>;
}

export function IconCheck(p: IconProps) {
  return <Icon {...p}><path d="M3 8.5L6.5 12 13 4.5" /></Icon>;
}

export function IconArrowRight(p: IconProps) {
  return <Icon {...p}><path d="M3 8h10M9 4l4 4-4 4" /></Icon>;
}

export function IconArrowLeft(p: IconProps) {
  return <Icon {...p}><path d="M13 8H3M7 4L3 8l4 4" /></Icon>;
}

export function IconX(p: IconProps) {
  return <Icon {...p}><path d="M4 4l8 8M12 4l-8 8" /></Icon>;
}

export function IconChevronDown(p: IconProps) {
  return <Icon {...p}><path d="M4 6l4 4 4-4" /></Icon>;
}

export function IconExternal(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M10 3h3v3" />
      <path d="M13 3l-5.5 5.5" />
      <path d="M13 10v2.5a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5v-9a.5.5 0 01.5-.5H6" />
    </Icon>
  );
}

export function IconClipboard(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="4" y="3" width="8" height="11" rx="1" />
      <path d="M6 2h4v2H6z" />
    </Icon>
  );
}

export function IconMail(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="2" y="4" width="12" height="9" rx="1" />
      <path d="M2.5 5l5.5 4.5L13.5 5" />
    </Icon>
  );
}

export function IconSpinner({ size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{ animation: "ckb-spin 800ms linear infinite" }}
      {...rest}
    >
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" />
      <path
        d="M13.5 8a5.5 5.5 0 00-5.5-5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
